import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

export const languages = {
  'pt-BR': { name: 'Português' },
  'en': { name: 'English' },
  'es': { name: 'Español' },
  'fr': { name: 'Français' },
} as const;

export type LanguageCode = keyof typeof languages;

// Resources are split per feature into locales/{lng}/{namespace}.json. Vite's
// glob import bundles them at build time and lets us assemble the i18next
// resource tree without 100+ manual imports. Eager = bundled into the main
// chunk (synchronous at init → no cold-start flash). Heavy, behind-auth
// namespaces can later be moved to a lazy backend per route.
const modules = import.meta.glob<{ default: Record<string, unknown> }>(
  './locales/*/*.json',
  { eager: true },
);

const resources: Record<string, Record<string, Record<string, unknown>>> = {};
for (const [path, mod] of Object.entries(modules)) {
  const m = path.match(/\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!m) continue;
  const [, lng, ns] = m;
  (resources[lng] ??= {})[ns] = mod.default;
}

// All namespaces present (derived from the source language).
const namespaces = Object.keys(resources['pt-BR'] ?? {});

// We only ship 4 exact bundles ('pt-BR', 'en', 'es', 'fr'). A detected region
// variant like 'fr-FR', 'en-US' or a bare 'pt' has NO matching resource, so
// react-i18next would render the fallback (pt-BR) until it resolved — a visible
// PT flash for users whose browser/locale uses a regional code. Normalizing
// every detected value down to one of our 4 codes (by language prefix) makes
// the very first render land on the right bundle.
function normalizeLng(raw?: string | null): LanguageCode | undefined {
  if (!raw) return undefined;
  const low = raw.toLowerCase();
  if (low.startsWith('pt')) return 'pt-BR';
  if (low.startsWith('en')) return 'en';
  if (low.startsWith('es')) return 'es';
  if (low.startsWith('fr')) return 'fr';
  return undefined;
}

// Resolve the initial language synchronously (same source the index.html boot
// loader reads), so i18n starts already on the persisted/detected language —
// no async detection window, no region-variant mismatch.
const storedLng = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null;
const navLng = typeof navigator !== 'undefined' ? navigator.language : null;
const initialLng = normalizeLng(storedLng) ?? normalizeLng(navLng) ?? 'pt-BR';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: namespaces,
    lng: initialLng,
    supportedLngs: ['pt-BR', 'en', 'es', 'fr'],
    fallbackLng: 'pt-BR',
    defaultNS: 'common',

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
      // Collapse region variants ('fr-FR' → 'fr', 'pt' → 'pt-BR') to our bundles.
      convertDetectedLanguage: (lng: string) => normalizeLng(lng) ?? 'pt-BR',
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    react: {
      useSuspense: false,
    },
  });

// Keep <html lang> in sync with the active language (SEO + a11y). The static
// default in index.html is pt-BR; this updates it on load and on switch.
if (typeof document !== 'undefined') {
  const applyLang = (lng: string) => {
    document.documentElement.lang = lng?.toLowerCase().startsWith('pt') ? 'pt-BR' : (lng || 'pt-BR');
  };
  applyLang(i18n.language);
  i18n.on('languageChanged', applyLang);
}

export default i18n;
